import { TimelineLite, Power2 } from 'gsap/all'
import Hammer from 'hammerjs'

import { getDocumentElements, getViewportWidth } from '../utils'

const [galleries] = getDocumentElements([{ className: 'js-hel-gallery', getAllElements: true }])

const init = () => {
    if (galleries) {
        Array.from(galleries).map(gallery => {
            const gallerySlides = gallery.querySelectorAll('.js-hel-gallery__slide')
            const innerTextContainers = gallery.querySelectorAll(
                '.js-hel-gallery__text-container--inner'
            )

            if (gallerySlides.length > 1) {
                const switchButtons = []

                // -------------------- GENERATE SWITCH BUTTONS --------------------

                const switchEl = document.createElement('section')
                if (gallery.classList.contains('hel-gallery')) {
                    switchEl.classList.add('hel-gallery__switch')
                } else if (gallery.classList.contains('hel-gallery--alt')) {
                    switchEl.classList.add('hel-gallery__switch--alt')
                }

                gallery.appendChild(switchEl)

                Array.from(gallerySlides).map(slide => {
                    const switchButton = document.createElement('span')
                    switchButton.classList.add('hel-gallery__switch-button')

                    slide.classList.contains('is-active') && switchButton.classList.add('is-active')

                    switchButtons.push(switchButton)
                    switchEl.appendChild(switchButton)
                })

                // -------------------- SWITCH BUTTON CLICK --------------------

                let isAnimationActive = false

                const setAnimation = ({ status }) => {
                    isAnimationActive = status
                }

                const switchSlideAnimation = ({ oldSlideIndex, newSlideIndex, direction }) => {
                    const switchSlideAnimationTimeline = new TimelineLite()

                    switchSlideAnimationTimeline.timeScale(1 / 1)

                    const fadeDuration = 1

                    const containerFadeDistance = direction * 100
                    const textFadeDistance = direction * 100 * 0.5

                    switchSlideAnimationTimeline
                        .call(() => setAnimation({ status: true }))
                        .fromTo(
                            innerTextContainers[oldSlideIndex],
                            fadeDuration,
                            { autoAlpha: 1, x: '0%' },
                            {
                                autoAlpha: 0,
                                x: `${textFadeDistance * -1}%`,
                                ease: Power2.easeInOut,
                            },
                            'start'
                        )
                        .fromTo(
                            gallerySlides[oldSlideIndex],
                            fadeDuration,
                            { x: `0%` },
                            {
                                x: `${containerFadeDistance * -1}%`,
                                ease: Power2.easeInOut,
                            },
                            'start+=0.1'
                        )
                        .fromTo(
                            gallerySlides[newSlideIndex],
                            fadeDuration,
                            { x: `${containerFadeDistance}%` },
                            { x: `0%`, ease: Power2.easeInOut },
                            'start+=0.1'
                        )
                        .fromTo(
                            innerTextContainers[newSlideIndex],
                            fadeDuration,
                            { autoAlpha: 0, x: `${textFadeDistance}%` },
                            {
                                autoAlpha: 1,
                                x: `0%`,
                                ease: Power2.easeInOut,
                            },
                            'start+=0.2'
                        )
                        .call(() => setAnimation({ status: false }))
                }

                const switchSlides = ({
                    oldSlideIndex = null,
                    direction = null,
                    newSlideIndex,
                }) => {
                    if (isAnimationActive) {
                        return
                    }

                    if (!oldSlideIndex) {
                        oldSlideIndex = Array.from(switchButtons).findIndex(switchButton =>
                            switchButton.classList.contains('is-active')
                        )
                    }

                    if (oldSlideIndex === newSlideIndex) {
                        return
                    }

                    if (!direction) {
                        direction = newSlideIndex > oldSlideIndex ? 1 : -1
                    }

                    switchButtons[oldSlideIndex].classList.remove('is-active')
                    switchButtons[newSlideIndex].classList.add('is-active')

                    switchSlideAnimation({ oldSlideIndex, newSlideIndex, direction })
                }

                Array.from(switchButtons).map((switchButton, switchButtonIndex) => {
                    switchButton.addEventListener('click', () =>
                        switchSlides({ newSlideIndex: switchButtonIndex })
                    )
                })

                const gallerySwiper = new Hammer(gallery)
                gallerySwiper.on('swipe', e => {
                    const oldSlideIndex = Array.from(switchButtons).findIndex(switchButton =>
                        switchButton.classList.contains('is-active')
                    )
                    const viewportWidth = getViewportWidth()

                    let newSlideIndex
                    let direction
                    let tempo = 3.4

                    if (viewportWidth >= 1280) return
                    else if (viewportWidth >= 980) tempo = 1.9
                    else if (viewportWidth >= 720) tempo = 2.3
                    else if (viewportWidth >= 480) tempo = 2.5

                    if (e.direction === 4) {
                        newSlideIndex =
                            oldSlideIndex === 0
                                ? Array.from(switchButtons).length - 1
                                : oldSlideIndex - 1
                        direction = -1
                    } else if (e.direction === 2) {
                        newSlideIndex =
                            oldSlideIndex === Array.from(switchButtons).length - 1
                                ? 0
                                : oldSlideIndex + 1
                        direction = 1
                    }

                    if (e.direction === 4 || e.direction === 2) {
                        setTimeout(() => {
                            switchSlides({
                                oldSlideIndex,
                                direction,
                                newSlideIndex,
                            })
                        }, 0)

                        window.clearTimeout(galleryAutoSwitchTimeout)
                        galleryAutoSwitchFunction()
                    }
                })

                // -------------------- SLIDE AUTO SWITCHING --------------------

                let galleryAutoSwitchTimeout
                const autoSwitchMinTime = 4800
                const autoSwitchMaxTime = 6500
                const autoSwitchRandomTime =
                    Math.random() * (autoSwitchMaxTime - autoSwitchMinTime) + autoSwitchMinTime

                gallery.addEventListener('mouseover', () =>
                    window.clearTimeout(galleryAutoSwitchTimeout)
                )
                gallery.addEventListener('mouseleave', () => galleryAutoSwitchFunction())

                const galleryAutoSwitchFunction = () => {
                    galleryAutoSwitchTimeout = window.setTimeout(() => {
                        const oldSlideIndex = Array.from(switchButtons).findIndex(switchButton =>
                            switchButton.classList.contains('is-active')
                        )
                        if (Array.from(switchButtons).length > oldSlideIndex + 1) {
                            setTimeout(() => {
                                switchSlides({
                                    switchButton: switchButtons[oldSlideIndex],
                                    newSlideIndex: oldSlideIndex + 1,
                                    direction: 1, // Always forward
                                })
                            }, 0)
                        } else {
                            setTimeout(() => {
                                switchSlides({
                                    switchButton: switchButtons[oldSlideIndex],
                                    newSlideIndex: 0,
                                    direction: 1, // Always forward
                                })
                            }, 0)
                        }
                        galleryAutoSwitchFunction()
                    }, autoSwitchRandomTime)
                }

                galleryAutoSwitchFunction()
            }
        })
    }
}

export { init }
